import { BottomOrRegularModal, Button } from '@popsure/dirty-swan';
import { getEligibilityForShortTerm } from 'components/PrivateQuoteWidget/util';
import { useTranslation } from 'next-i18next';
import AnimateHeight from 'react-animate-height';

import styles from './ShortTermModal.module.scss';

export const ShortTermModal = ({
  isFamily,
  isOpen,
  onClose,
  wantsToCoverFamily,
  setWantsToCoverFamily,
  hasEUPassport,
  hasPermanentResidencyPermit,
  isPlanningToStayMoreThan5Years,
  setHasEUPassport,
  setHasPermanentResidencyPermit,
  setIsPlanningToStayMoreThan5Years,
  onApplyDiscount,
}: {
  isFamily?: boolean;
  wantsToCoverFamily?: boolean;
  setWantsToCoverFamily: (value: boolean) => void;
  hasEUPassport?: boolean;
  hasPermanentResidencyPermit?: boolean;
  isPlanningToStayMoreThan5Years?: boolean;
  setHasEUPassport: (value: boolean) => void;
  setHasPermanentResidencyPermit: (value: boolean) => void;
  setIsPlanningToStayMoreThan5Years: (value: boolean) => void;
  onApplyDiscount: () => void;
} & Omit<React.ComponentProps<typeof BottomOrRegularModal>, 'children'>) => {
  const { t } = useTranslation();

  const hasAnsweredAllQuestions =
    hasEUPassport ||
    hasPermanentResidencyPermit ||
    isPlanningToStayMoreThan5Years !== undefined;

  const isFamilyEligibleForShortTerm = getEligibilityForShortTerm({
    hasEUPassport,
    hasPermanentResidencyPermit,
    isPlanningToStayMoreThan5Years,
  });

  const showFamilyQuestions = isFamily || wantsToCoverFamily;

  return (
    <>
      <BottomOrRegularModal
        isOpen={isOpen}
        title={t(
          'privatehealth:calculator.shortTermModal.title',
          'Short-term insurance'
        )}
        onClose={onClose}
      >
        <div className={`p24 pt16 ${styles.modalContent}`}>
          {!showFamilyQuestions ? (
            <p className="p-p">
              {t(
                'privatehealth:calculator.shortTermModal.intro',
                'Let’s check if you’re eligible for discounted plans.'
              )}
            </p>
          ) : (
            <p className="p-p">
              {t(
                'privatehealth:calculator.shortTermModal.intro.family',
                'You might be eligible for a slightly different tariff if you’re only staying in Germany for a few years.'
              )}
            </p>
          )}
          {!isFamily && (
            <>
              <legend className="p-p tc-grey-600 w100 mt24 mb8">
                {t(
                  'privatehealth:calculator.shortTermModal.wantsToCoverFamily.question',
                  'Who would you like to insure?'
                )}
              </legend>
              <div className="d-flex f-wrap gap8 w100">
                <div className={styles.half}>
                  <input
                    name="COVER_MYSELF"
                    className="p-radio"
                    type="radio"
                    id="COVER_MYSELF"
                    value="COVER_MYSELF"
                    checked={
                      wantsToCoverFamily !== undefined && !wantsToCoverFamily
                    }
                    onChange={() => {
                      setWantsToCoverFamily(false);
                    }}
                  />
                  <label
                    htmlFor="COVER_MYSELF"
                    className="p-label p-label--bordered"
                  >
                    {t(
                      'privatehealth:calculator.shortTermModal.wantsToCoverFamily.myself',
                      'Myself'
                    )}
                  </label>
                </div>
                <div className={styles.half}>
                  <input
                    name="COVER_FAMILY"
                    className="p-radio"
                    type="radio"
                    id="COVER_FAMILY"
                    value="COVER_FAMILY"
                    checked={wantsToCoverFamily}
                    onChange={() => {
                      setWantsToCoverFamily(true);
                    }}
                  />
                  <label
                    htmlFor="COVER_FAMILY"
                    className="p-label p-label--bordered"
                  >
                    {t(
                      'privatehealth:calculator.shortTermModal.wantsToCoverFamily.family',
                      'My family'
                    )}
                  </label>
                </div>
              </div>
            </>
          )}
          <AnimateHeight
            duration={300}
            height={isFamily || wantsToCoverFamily !== undefined ? 'auto' : 0}
            className="w100"
          >
            <legend className="p-p tc-grey-600 w100 mt24 mb8">
              {!showFamilyQuestions
                ? t(
                    'privatehealth:calculator.shortTermModal.euPassport.question',
                    'Do you hold an an EU/EEA passport?'
                  )
                : t(
                    'privatehealth:calculator.shortTermModal.euPassport.question.family',
                    'Do any of you hold an EU/EEA passport?'
                  )}
            </legend>
            <div className="d-flex f-wrap gap8 w100">
              <div className={styles.half}>
                <input
                  name="HAS_EU_PASSPORT"
                  className="p-radio"
                  type="radio"
                  id="HAS_EU_PASSPORT"
                  value="HAS_EU_PASSPORT"
                  checked={!!hasEUPassport}
                  onChange={() => {
                    setHasEUPassport(true);
                  }}
                />
                <label
                  htmlFor="HAS_EU_PASSPORT"
                  className="p-label p-label--bordered"
                >
                  {t('privatehealth:calculator.yes', 'Yes')}
                </label>
              </div>
              <div className={styles.half}>
                <input
                  name="HAS_NO_EU_PASSPORT"
                  className="p-radio"
                  type="radio"
                  id="HAS_NO_EU_PASSPORT"
                  value="HAS_NO_EU_PASSPORT"
                  checked={hasEUPassport !== undefined && !hasEUPassport}
                  onChange={() => {
                    setHasEUPassport(false);
                  }}
                />
                <label
                  htmlFor="HAS_NO_EU_PASSPORT"
                  className="p-label p-label--bordered"
                >
                  {t('privatehealth:calculator.no', 'No')}
                </label>
              </div>
            </div>
          </AnimateHeight>
          <AnimateHeight
            duration={300}
            height={hasEUPassport !== undefined && !hasEUPassport ? 'auto' : 0}
            className="w100"
          >
            <>
              <legend className="p-p tc-grey-600 w100 mt24 mb8">
                {!showFamilyQuestions
                  ? t(
                      'privatehealth:calculator.shortTermModal.permanentResidencyPermit.question',
                      'Do you have a permanent residence permit in Germany?'
                    )
                  : t(
                      'privatehealth:calculator.shortTermModal.permanentResidencyPermit.question.family',
                      'Do any of you have a permanent residence permit in Germany?'
                    )}
              </legend>
              <div className="d-flex f-wrap gap8 w100">
                <div className={styles.half}>
                  <input
                    name="HAS_PERMANENT_RESIDENCY_PERMIT"
                    className="p-radio"
                    type="radio"
                    id="HAS_PERMANENT_RESIDENCY_PERMIT"
                    value="HAS_PERMANENT_RESIDENCY_PERMIT"
                    checked={!!hasPermanentResidencyPermit}
                    onChange={() => {
                      setHasPermanentResidencyPermit(true);
                    }}
                  />
                  <label
                    htmlFor="HAS_PERMANENT_RESIDENCY_PERMIT"
                    className="p-label p-label--bordered"
                  >
                    {t('privatehealth:calculator.yes', 'Yes')}
                  </label>
                </div>
                <div className={styles.half}>
                  <input
                    name="HAS_NO_PERMANENT_RESIDENCY_PERMIT"
                    className="p-radio"
                    type="radio"
                    id="HAS_NO_PERMANENT_RESIDENCY_PERMIT"
                    value="HAS_NO_PERMANENT_RESIDENCY_PERMIT"
                    checked={
                      hasPermanentResidencyPermit !== undefined &&
                      !hasPermanentResidencyPermit
                    }
                    onChange={() => {
                      setHasPermanentResidencyPermit(false);
                    }}
                  />
                  <label
                    htmlFor="HAS_NO_PERMANENT_RESIDENCY_PERMIT"
                    className="p-label p-label--bordered"
                  >
                    {t('privatehealth:calculator.no', 'No')}
                  </label>
                </div>
              </div>
            </>
          </AnimateHeight>
          <AnimateHeight
            duration={300}
            height={
              hasEUPassport !== undefined &&
              !hasEUPassport &&
              hasPermanentResidencyPermit !== undefined &&
              !hasPermanentResidencyPermit
                ? 'auto'
                : 0
            }
            className="w100"
          >
            <>
              <legend className="p-p tc-grey-600 w100 mt24 mb8">
                {!showFamilyQuestions
                  ? t(
                      'privatehealth:calculator.shortTermModal.relocatedMoreThan4YearsAgo..question',
                      'Did you relocate to Germany more than 4 years ago?'
                    )
                  : t(
                      'privatehealth:calculator.shortTermModal.relocatedMoreThan4YearsAgo.question.dependent',
                      'Did any of you relocate to Germany more than 4 years ago?'
                    )}
              </legend>
              <div className="d-flex f-wrap gap8 w100">
                <div className={styles.half}>
                  <input
                    name="RELOCATED_MORE_THAN_4_YEARS_AGO"
                    className="p-radio"
                    type="radio"
                    id="RELOCATED_MORE_THAN_4_YEARS_AGO"
                    value="RELOCATED_MORE_THAN_4_YEARS_AGO"
                    checked={!!isPlanningToStayMoreThan5Years}
                    onChange={() => {
                      setIsPlanningToStayMoreThan5Years(true);
                    }}
                  />
                  <label
                    htmlFor="RELOCATED_MORE_THAN_4_YEARS_AGO"
                    className="p-label p-label--bordered"
                  >
                    {t('privatehealth:calculator.yes', 'Yes')}
                  </label>
                </div>
                <div className={styles.half}>
                  <input
                    name="DOESNT_PLAN_TO_STAY_LONGER_THAN_5_YEARS"
                    className="p-radio"
                    type="radio"
                    id="DOESNT_PLAN_TO_STAY_LONGER_THAN_5_YEARS"
                    value="DOESNT_PLAN_TO_STAY_LONGER_THAN_5_YEARS"
                    checked={
                      isPlanningToStayMoreThan5Years !== undefined &&
                      !isPlanningToStayMoreThan5Years
                    }
                    onChange={() => {
                      setIsPlanningToStayMoreThan5Years(false);
                    }}
                  />
                  <label
                    htmlFor="DOESNT_PLAN_TO_STAY_LONGER_THAN_5_YEARS"
                    className="p-label p-label--bordered"
                  >
                    {t('privatehealth:calculator.no', 'No')}
                  </label>
                </div>
              </div>
            </>
          </AnimateHeight>
          <AnimateHeight
            duration={300}
            height={
              hasAnsweredAllQuestions && !isFamilyEligibleForShortTerm
                ? 'auto'
                : 0
            }
            className="w100"
          >
            <div
              className={`bg-blue-100  p24 mt24 br8 ${styles.notEligibleBox}`}
            >
              <p className="p-p fw-bold">Unfortunately, you’re not eligible</p>
              <p className="p-p mt16">
                We can only offer short-term insurance to non-EU citizens who
                have a temporary residence permit and have been in Germany for
                less than 4 years.
              </p>
            </div>
          </AnimateHeight>
          <AnimateHeight
            duration={300}
            height={isFamilyEligibleForShortTerm ? 'auto' : 0}
            className="w100"
          >
            <div className={`bg-green-100  p24 mt24 br8 ${styles.eligibleBox}`}>
              <p className="p-p fw-bold">You’re eligible!</p>
              <p className="p-p mt16">
                Short-term plans are valid for all visas and have a similar
                coverage, but are up to 50% cheaper because they don’t include
                old age savings. Old age savings ensure a stable insurance price
                when you retire, but aren’t necessary for those who plan on
                leaving Germany.
              </p>
              <p className="p-p mt16">
                If you stay in Germany for more than 5 years, we’ll
                automatically help you switch to a plan with old age savings.
              </p>
            </div>
          </AnimateHeight>
          <div className="mt24 d-flex f-wrap gap16">
            {isFamilyEligibleForShortTerm && (
              <Button
                className="wmn3"
                variant="filledColor"
                onClick={() => {
                  onApplyDiscount();
                  onClose();
                }}
              >
                Apply discount
              </Button>
            )}
            <Button className="wmn3" variant="filledGray" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </BottomOrRegularModal>
    </>
  );
};
